import React from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import ButtonBase from '@material-ui/core/ButtonBase';

// Data
import { useSelector, useDispatch } from 'react-redux';
import { doAccountDiscovery } from 'components/Dialogs/AccountDiscovery/actions';

// CUSTOM
import fi from 'assets/onboarding/fi.svg';
import QButton from 'components/QButton';

import AccountChip from './AccountChip';
import { getAccountChipData } from './selectors';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: 'calc(100% + 24px)',
  },
  black: {
    color: theme.palette.greyScale[0],
  },
  grey: {
    color: theme.palette.greyScale[1],
  },
  bolder: {
    color: theme.palette.greyScale[0],
    fontWeight: 500,
  },
  spacedRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  listWrapper: {
    marginTop: 8,
    flexGrow: 1,
    position: 'relative',
    overflow: 'hidden',
    height: 'calc(100% - 72px)',
  },
  modelList: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    overflow: 'scroll',
    height: '100%',
    paddingBottom: 16,
  },
  bottomFade: {
    zIndex: 1,
    position: 'absolute',
    bottom: 0,
    height: 24,
    width: '100%',
    backgroundImage: `linear-gradient(${theme.applyOpacityToHex(theme.palette.greyScale[7], 0)}, ${theme.palette.greyScale[7]})`,
  },

  show: {
    opacity: 1,
    transition: 'opacity 0.4s ease',
  },
  hide: {
    opacity: 0,
    transition: 'opacity 0.4s ease',
  },

  // adder styles
  chip: {
    overflow: 'hidden',
    width: 'calc(50% - 16px)',
    margin: 8,
    height: 80,
    borderRadius: 8,
    backgroundColor: theme.applyOpacityToHex(theme.palette.secondary.main, 0.8),
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.palette.greyScale[7],
    position: 'relative',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: theme.applyOpacityToHex(theme.palette.secondary.dark, 0.8),
    },
  },
  slash: {
    position: 'absolute',
    left: -10,
    height: 92,
    width: 52,
    backgroundColor: theme.applyOpacityToHex(theme.palette.secondary.dark, 0.2),
    transform: 'rotate(-12deg)',
  },
  icon: {
    height: 48,
    width: 48,
    borderRadius: 8,
    padding: 8,
    zIndex: 1,
    backgroundColor: theme.applyOpacityToHex(theme.palette.secondary.dark, 0.6),
  },
  addName: {
    marginRight: 80,
    fontWeight: 500,
  },
  bigAdd: {
    height: 32,
    width: 32,
  },
}));

// Functional Component
// - - - - - - - - - - - - - - - - - - - - - - - - - -
export default function AccountPage(props) {
  const classes = useStyles();
  const { accounts, style } = props;
  const dispatch = useDispatch();

  const dataToRender = useSelector((state) => getAccountChipData(state, accounts));

  const addBank = () => dispatch(doAccountDiscovery({ initiator: 'getting started', mode: 'GSF' }));

  //     RENDERERS
  // ------------------------------------------------------
  const renderAdd = () =>
    <ButtonBase
      key="Add-Bank-Button"
      id="Add-Bank-Button"
      focusRipple
      className={classes.chip}
      onClick={addBank}
    >
      <div className={classes.slash}/>

      <img alt="Add Banner" src={fi} className={classes.icon}/>
      <Typography variant="body1" className={classes.addName}>
        ADD BANK
      </Typography>
      <AddIcon className={classes.bigAdd}/>
    </ButtonBase>;


  //     MAIN RETURN
  // ------------------------------------------------------------------------
  const smallAdd = dataToRender.size > 5;
  return (
    <div className={classes.root} style={style}>
      <Typography variant="h6" className={classes.black}>
        {`Let's Add Your Banks`}
      </Typography>
      <div className={classes.spacedRow} style={{ marginTop: -4 }}>
        <Typography variant="body1" className={classes.grey}>
          Add your accounts to see all of your finances in one place
        </Typography>
        <QButton
          className={smallAdd ? classes.show : classes.hide}
          disabled={!smallAdd}
          onClick={addBank}
        >
          ADD BANK <AddIcon style={{ marginLeft: 4 }}/>
        </QButton>
      </div>

      <div className={classes.listWrapper}>
        <div className={classes.modelList}>
          {dataToRender.map((data) => <AccountChip key={data.id} data={data} />)}
          {renderAdd()}
        </div>

        <div className={classes.bottomFade}/>
      </div>
    </div>
  );
}


AccountPage.propTypes = {
  accounts: PropTypes.object,
  style: PropTypes.object,
};
